import '../../../common/utils/times'
import '../../../scss/bakery/components/production-view.scss'

import {
  Body2,
  Box,
  Button,
  Container,
  Display2,
  Flex,
  Grid,
  GridItem,
} from '@sainsburys-tech/fable'
import dayjs from 'dayjs'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'

import { isFeatureEnabled } from '../../../common/api/featureApi'
import { useApiClient } from '../../../common/AppContext/appContext'
import InfoBanner from '../../../common/components/Banners/InfoBanner'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import ProgressLoading from '../../../common/components/ProgressLoading'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { Feature } from '../../../common/enums/FeatureEnum'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import {
  toIsoDateStringFrom,
  toNthDayOfWeekFullDatetimeString,
} from '../../../common/utils/times'
import { checkStore, getUserStore } from '../../../utils/localStore'
import { getMixes, updateMixStatus } from '../../api/mixApi'
import { MixStageEnum } from '../../enums/MixStageEnum'
import { IMix } from '../../types/IMix'
import AlertMixStatusModal from './AlertMixStatusModal'
import WaveMixCard from './WaveMixCard'

interface IWaveState {
  plannerGroupId: number
  ovenSettingId: number
  ovenTypeDescription: string
  wave: number
}

const ProductionView = () => {
  // 1. Context and Router hooks (external dependencies)
  const { dispatch } = useContext(Context)
  const navigate = useNavigate()
  const apiClient = useApiClient()

  // 2. Memoized values (computed once)
  const store = useMemo(() => getUserStore(), [])

  // 3. Component state (local state management)
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [mixes, setMixes] = useState<IMix[]>([])
  const [currentMixId, setCurrentMixId] = useState<number>(0)
  const [canAutoScroll, setCanAutoScroll] = useState(true)
  const [isAlertMixStatusModalOpened, setIsAlertMixStatusModalOpened] =
    useState(false)
  const [mixStatusAlertMessage, setMixStatusAlertMessage] = useState('')

  // 4. Memoized callbacks (functions that depend on state)
  const { handleError, resetError } = useErrorHandler(setErrorMessage)

  const scrollToLastMix = useCallback(() => {
    if (!canAutoScroll || !currentMixId) {
      return
    }

    const element = document.querySelector(`#MixId_${currentMixId}`)
    element?.scrollIntoView({
      behavior: 'auto',
      block: 'center',
      inline: 'nearest',
    })
  }, [canAutoScroll, currentMixId])

  const isMixNextDay = useCallback((mix: IMix): boolean => {
    const tomorrow = dayjs().add(1, 'day').format('YYYY/MM/DD')
    const mixDate = dayjs(mix.plan.planDate).format('YYYY/MM/DD')
    return tomorrow === mixDate
  }, [])

  // 5. Custom hooks (composed logic)
  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.Bakery,
      handleError,
    )

  // 6. Derived state (computed from other state)
  const isLoading = initLoading || isDataLoading

  // 7. Side effects (useEffect hooks)
  // Load mixes data
  useEffect(() => {
    const loadMixesData = async () => {
      setIsDataLoading(true)
      resetError()

      try {
        const [mixesData, isStoreZeroErrorsEnabled] = await Promise.all([
          getMixes(apiClient, store.storeId, new Date()),
          isFeatureEnabled(apiClient, Feature.StoreZeroErrors, 0),
        ])
        setMixes(mixesData)

        if (isStoreZeroErrorsEnabled) {
          const storeError = checkStore(store)
          if (storeError) {
            handleError(storeError)
          }
        }

        scrollToLastMix()
      } catch (e) {
        handleError(
          `${ErrorMessage.ProductionView.FailedToUpdate} - ${(e as Error).message}`,
        )
      } finally {
        setIsDataLoading(false)
      }
    }

    loadMixesData()
  }, [apiClient, store, currentMixId, handleError, resetError, scrollToLastMix])

  const getWavesForToday = () => {
    const wavesCurrentDay = mixes.filter((mix) => isMixNextDay(mix) === false)
    return getWaves(wavesCurrentDay)
  }

  const getWavesForTomorrow = () => {
    const wavesForTomorrow = mixes.filter((mix) => isMixNextDay(mix) === true)
    return getWaves(wavesForTomorrow)
  }

  const getWaveHeader = (mixesForWave: IMix[], waveNumber: number): string => {
    if (mixesForWave.some((mix) => isMixNextDay(mix))) {
      return 'Overnights'
    }
    if (mixesForWave.some((mix) => mix.isManuallyCreated)) {
      return `Wave ${waveNumber} (Additional Wave)`
    }
    return `Wave ${waveNumber}`
  }

  const isOvernightButCantStart = (mixesForWave: IMix[]): boolean =>
    mixesForWave.some((mix) => isMixNextDay(mix) && mix.plan.requiresMixRefresh)

  const displayForecastPendingWarning = (mixesForWave: IMix[]): any => {
    if (isOvernightButCantStart(mixesForWave)) {
      return (
        <InfoBanner
          message='Overnights is pending updates. Try again in 10 minutes.'
          data-testid='mix-overnights-warning'
        />
      )
    }
  }

  const getWaves = (wMixes: IMix[]) => {
    const distinctWaveNumbers = [...new Set(wMixes.map((mix) => mix.wave))]
    const waveComponents: JSX.Element[] = []
    const enabledOrCompletedMixList: IWaveState[] = []
    distinctWaveNumbers
      .sort((waveA, waveB) => waveA - waveB)
      .forEach((waveNumber) => {
        const mixesForWave = wMixes.filter((mix: IMix) => {
          const isIncomplete =
            mix.wave === waveNumber &&
            (mix.mixStatusStageId === (MixStageEnum.Pending as number) ||
              mix.mixStatusStageId === (MixStageEnum.InProgress as number))

          if (isIncomplete) {
            const ovenSettingIdInProcess =
              (mix.ovenSettingDetail && mix.ovenSettingDetail.ovenSettingId) ||
              0

            if (
              enabledOrCompletedMixList.findIndex(
                (ws) =>
                  ws.plannerGroupId === mix.plannerGroupId &&
                  ws.wave !== waveNumber &&
                  ws.ovenSettingId === ovenSettingIdInProcess,
              ) === -1
            ) {
              mix.enabled = true
              const newWaveState: IWaveState = {
                ovenSettingId: ovenSettingIdInProcess,
                ovenTypeDescription: '',
                plannerGroupId: mix.plannerGroupId,
                wave: waveNumber,
              }
              enabledOrCompletedMixList.push(newWaveState)
            }
          }

          return isIncomplete
        })

        if (mixesForWave.length > 0) {
          waveComponents.push(
            <Box
              className='wave-header'
              key={waveNumber}
              backgroundColor='primary'
              testid='wave-header'
            >
              <Box testid='wave-header-text' className='wave-header-text'>
                <Display2>{getWaveHeader(mixesForWave, waveNumber)}</Display2>
              </Box>
              {displayForecastPendingWarning(mixesForWave)}
              <Box className='wave-mix-card'>
                <WaveMixCard
                  mixesForWave={mixesForWave}
                  handleMixActions={handleMixNextActions}
                />
              </Box>
            </Box>,
          )
        }
      })

    return waveComponents
  }

  const getCompletedWavesForToday = () => {
    const wavesCurrentDay = mixes.filter((mix) => isMixNextDay(mix) === false)
    return getCompletedWaves(wavesCurrentDay)
  }

  const getCompletedWavesForTomorrow = () => {
    const wavesForTomorrow = mixes.filter((mix) => isMixNextDay(mix) === true)
    return getCompletedWaves(wavesForTomorrow)
  }

  const getCompletedWaves = (completedMixes: IMix[]) => {
    const distinctWaveNumbers = [
      ...new Set(completedMixes.map((mix) => mix.wave)),
    ]
    const waveComponents: JSX.Element[] = []
    distinctWaveNumbers
      .sort((a, b) => a - b)
      .forEach((waveNumber) => {
        const mixesForWave = completedMixes.filter(
          (mix: IMix) =>
            mix.wave === waveNumber &&
            mix.mixStatusStageId === (MixStageEnum.Completed as number),
        )

        if (mixesForWave.length > 0) {
          waveComponents.push(
            <Box
              key={waveNumber}
              backgroundColor='primary'
              className='wave-header'
            >
              <Box className='wave-header-text'>
                <Display2>{getWaveHeader(mixesForWave, waveNumber)}</Display2>
              </Box>
              <Box className='wave-mix-card'>
                <WaveMixCard
                  mixesForWave={mixesForWave}
                  handleMixActions={handleMixNextActions}
                />
              </Box>
            </Box>,
          )
        }
      })

    return waveComponents
  }

  const handleMixNextActions = async (mix: IMix, nextStatus?: MixStageEnum) => {
    setCanAutoScroll(false)

    if (nextStatus === MixStageEnum.Completed) {
      if (mix.mixStatusStageId === (MixStageEnum.Completed as number)) {
        setMixStatusAlertMessage('This wave has already been completed')
        setIsAlertMixStatusModalOpened(true)
        return
      }

      try {
        await updateMixStatus(
          apiClient,
          mix.planId,
          mix.mixId,
          nextStatus,
          mix.mixSkus,
          mix.rowVersion,
        )

        setCurrentMixId(mix.mixId)
      } catch (e) {
        const exceptionMessage = (e as Error).message
        if (exceptionMessage === 'maintenance') {
          window.location.href = `/service-unavailable?message=${exceptionMessage}`
        }
        handleError(ErrorMessage.ProductionView.FailedToUpdate, e as Error)
      }

      return
    }

    if (mix.mixStatusStageId === (MixStageEnum.Pending as number)) {
      if (!mix.enabled) {
        // it won't take a stage progress on a 'Preview Wave'
        navigate(
          `/mixskusview/${mix.mixId}/${toIsoDateStringFrom(mix.plan.planDate)}/true`,
        )
        return
      }

      setCurrentMixId(mix.mixId)
    }

    navigate(
      `/mixskusview/${mix.mixId}/${toIsoDateStringFrom(mix.plan.planDate)}/false`,
    )
  }

  return (
    <>
      {isLoading && (
        <ProgressLoading message='Loading...' testid='loading-indicator' />
      )}

      {!isLoading && (
        <>
          <Box padding='none' testid='production-view'>
            <Header
              title={headerPlannerTitles.bakery}
              navItems={headerNavigationItems}
              activeMenuType={Menu.ProductionView}
            />
            <Container className='c-common-main-view-content'>
              {!isLoading && errorMessage && (
                <ErrorRefresh message={errorMessage} />
              )}
              {!isLoading && !errorMessage && (
                <>
                  <Flex justifyContent='right'>
                    <Button
                      variant='secondary'
                      onClick={() => navigate(`/additionalwave`)}
                      testid='add-new-wave-button'
                    >
                      Create Additional Wave
                    </Button>
                  </Flex>
                  <Grid
                    cols={12}
                    colSize='min'
                    gap={'md'}
                    className='production-view-equal-columns'
                  >
                    <GridItem colSpan={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                      <Box testid='production-box' padding='none'>
                        <Display2 style={{ paddingBottom: '16px' }}>
                          Production
                        </Display2>
                        <div className='production-view-date'>
                          <Body2 style={{ fontWeight: 'normal' }}>
                            {`${toNthDayOfWeekFullDatetimeString()}.`}
                          </Body2>
                        </div>

                        {getWavesForToday()}
                        {getWavesForTomorrow()}
                      </Box>
                    </GridItem>
                    <GridItem colSpan={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                      <Box testid='production-completed-box' padding='none'>
                        <Display2 style={{ paddingBottom: '16px' }}>
                          Production Completed
                        </Display2>
                        <div
                          className='production-view-date production-view-date--placeholder'
                          aria-hidden='true'
                        >
                          {`${toNthDayOfWeekFullDatetimeString()}.`}
                        </div>
                        {getCompletedWavesForToday()}
                        {getCompletedWavesForTomorrow()}
                      </Box>
                    </GridItem>
                  </Grid>
                </>
              )}
            </Container>
          </Box>
          {isAlertMixStatusModalOpened && (
            <AlertMixStatusModal
              isOpened={isAlertMixStatusModalOpened}
              setIsOpened={setIsAlertMixStatusModalOpened}
              message={mixStatusAlertMessage}
            />
          )}
        </>
      )}
    </>
  )
}

export default ProductionView
